<template>
    <base-demo-flow
        :phone_key="phone_key"
        :phone_mode="phone_mode"
        :browser_content_key="current_step"
        business_name="Barrel Creek Vineyards"
        phone_number="(555) 123-4567"
        browser_url="barrelcreek.com/confirm/abc123"
        phone_label="Sarah's Phone"
        :current_step_number="current_step_number"
        :total_steps="total_steps"
        :progress_percentage="progress_percentage"
        :current_step_name="current_step_name"
        :can_go_previous="can_go_previous"
        :can_go_next="can_go_next"
        @restart="resetDemo"
        @previous="goToPreviousStep"
        @next="goToNextStep"
    >
        <template #explainer>
            <explainer-text-message
                v-if="current_step === 'text-message'"
                :order_data="order_data"
            />

            <explainer-order-confirmation-form
                v-if="current_step === 'confirmation-form-correct'"
                :order_data="order_data"
            />

            <explainer-change-request-form
                v-if="current_step === 'change-form'"
                :order_data="order_data"
            />

            <explainer-completion
                v-if="['complete-correct', 'complete-incorrect'].includes(current_step)"
                :order_data="order_data"
                :submission_data="submission_data"
                :is_change_request="current_step === 'complete-incorrect'"
                :show_restart="current_step === 'complete-incorrect'"
                @reset="resetDemo"
            />
        </template>

        <template #phone-messages>
            <mock-text-conversation :messages="conversation" />
        </template>

        <template #phone-browser>
            <mock-order-confirmation-form
                v-if="['confirmation-form-correct', 'confirmation-form-incorrect'].includes(current_step)"
                :order_data="order_data"
                :allow_change_request="current_step === 'confirmation-form-incorrect'"
                @need-change="handleNeedChange"
                @submitted="handleConfirmationSubmit"
                @back-to-text="handleBackToText"
            />

            <mock-change-request-form
                v-else-if="current_step === 'change-form'"
                :order_data="order_data"
                @submitted="handleChangeSubmit"
                @reset="resetDemo"
            />

            <completion-demo
                v-if="['complete-correct', 'complete-incorrect'].includes(current_step)"
                :submission_data="submission_data"
                :is_change_request="current_step === 'complete-incorrect'"
                @reset="resetDemo"
            />
        </template>
    </base-demo-flow>
</template>

<script>
import BaseDemoFlow from '@/components/demos/shared/base-demo-flow.vue';
import DemoStepManager from '@/mixins/demo-step-manager.js';

import ExplainerTextMessage from '@/components/demos/order-confirmation/explainer-text-message.vue';
import ExplainerOrderConfirmationForm from '@/components/demos/order-confirmation/explainer-order-confirmation-form.vue';
import ExplainerChangeRequestForm from '@/components/demos/order-confirmation/explainer-change-request-form.vue';
import ExplainerCompletion from '@/components/demos/order-confirmation/explainer-completion.vue';

import MockTextConversation from '@/components/demos/shared/mock-text-conversation.vue';
import MockChangeRequestForm from '@/components/demos/order-confirmation/mock-change-request-form.vue';
import MockOrderConfirmationForm from '@/components/demos/order-confirmation/mock-order-confirmation-form.vue';
import CompletionDemo from '@/components/demos/order-confirmation/completion-demo.vue';

export default {
    name: 'DemoOrderConfirmationFlow',

    mixins: [DemoStepManager],

    components: {
        BaseDemoFlow,
        ExplainerTextMessage,
        ExplainerOrderConfirmationForm,
        ExplainerChangeRequestForm,
        ExplainerCompletion,
        MockTextConversation,
        MockOrderConfirmationForm,
        MockChangeRequestForm,
        CompletionDemo
    },

    data() {
        return {
            order_data: {
                order_number: 'BC-2025-0813',
                customer_name: 'Sarah Johnson',
                customer_nickname: 'Sarah',
                email: 'sarah.johnson@email.com',
                phone: '(555) 123-4567',
                delivery_method: 'Shipping',
                shipping_address: {
                    line1: '456 Oak Avenue',
                    line2: '',
                    city: 'Napa',
                    state: 'CA',
                    zip: '94558'
                }
            }
        }
    },

    computed: {
        conversation() {
            return [
                {
                    type: 'customer',
                    text: `Hi ${this.order_data.customer_nickname}! This is Barrel Creek. Thank you for your recent order! When you have a moment, please take a second to review and confirm your order details and shipping address. You can reply STOP anytime to opt out.`,
                    link: {
                        url: '#',
                        text: 'https://barrelcreek.com/confirm/abc123',
                        preventDefault: true,
                        callback: this.clickTextMessageLink
                    },
                    time: '2:34 PM'
                }
            ];
        }
    },

    methods: {
        getStepConfig() {
            return {
                initial_step: 'text-message',
                initial_phone_mode: 'messages',
                steps: ['text-message', 'confirmation-form-correct', 'complete-correct', 'change-form', 'complete-incorrect'],
                step_names: {
                    'text-message': 'SMS Notification',
                    'confirmation-form-correct': 'Customer Confirmation',
                    'complete-correct': 'Consent Gathered',
                    'change-form': 'Change Request',
                    'complete-incorrect': 'Completion',
                },
                step_definitions: {
                    'text-message': {
                        phone_mode: 'messages'
                    },
                    'confirmation-form-correct': {
                        phone_mode: 'browser'
                    },
                    'complete-correct': {
                        phone_mode: 'browser',
                        requires_submission_data: true,
                        default_submission_data: {
                            type: 'confirmation',
                            message: 'Demo navigation - order confirmed'
                        }
                    },
                    'change-form': {
                        phone_mode: 'browser'
                    },
                    'complete-incorrect': {
                        phone_mode: 'browser'
                    }
                }
            };
        },

        clickTextMessageLink() {
            this.navigateToStep('confirmation-form-correct');
        },

        handleNeedChange() {
            this.navigateToStep('change-form');
        },

        handleBackToText() {
            this.navigateToStep('text-message');
        },

        handleConfirmationSubmit(data) {
            this.handleStepSubmission(data, 'complete-correct');
        },

        handleChangeSubmit(data) {
            this.handleStepSubmission(data, 'complete-incorrect');
        }
    },

    mounted() {
        document.title = "Order Confirmation Demo | RedChirp";
        this.initializeSteps(this.getStepConfig());
    }
}
</script>

<style lang="scss" scoped></style>